// @src/pre-deployment/src/components/navigation/AppHomepage.js v4.0
//
// v4.0: Visual continuity with LevelPage — uses level-main-content, chapter-* classes
// - Same layout/gridding as LevelPage (level-brand, chapter-title-box, chapter-menu-vertical, etc.)
// - Dynamic background: chapter-background-layer, chapter-gradient-overlay
// - Level change via 이전/다음 arrows; sidebar for font/settings

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';

import './AppHomepage.css';
import Sidebar from './Sidebar';

const FALLBACK_IMAGE = '/img/level_1.jpg';

const levels = [
  { level: 0, korean: '0급', image: '/img/level_1.jpg', title: 'Hangul Practice', color: '#FF6B6B' },
  { level: 1, korean: '1급', image: '/img/level_1.jpg', title: 'Beginner', color: '#ff8a00' },
  { level: 2, korean: '2급', image: '/img/level_2.jpg', title: 'Elementary', color: '#4ECDC4' },
  { level: 3, korean: '3급', image: '/img/level_3.jpg', title: 'Pre-Intermediate', color: '#667eea' },
  { level: 4, korean: '4급', image: '/img/level_4.jpg', title: 'Intermediate', color: '#ff8a00' },
  { level: 5, korean: '5급', image: '/img/level_5.jpg', title: 'Upper-Intermediate', color: '#4facfe' },
  { level: 6, korean: '6급', image: '/img/level_6.jpg', title: 'Advanced', color: '#ff8a00' }
];

function getMediaType(url) {
  const ext = (url || '').split('.').pop()?.toLowerCase();
  if (['mp4', 'webm'].includes(ext)) return 'video';
  return 'image';
}

const AppHomepage = () => {
  useAuth();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [componentError, setComponentError] = useState(null);
  const [selectedLevelIndex, setSelectedLevelIndex] = useState(0);

  const currentLevel = levels[selectedLevelIndex];

  const handleEnter = useCallback(() => {
    try {
      navigate(`/level/${currentLevel.level}`);
    } catch (error) {
      console.error('Navigation error:', error);
      setComponentError(error);
      navigate('/app-homepage');
    }
  }, [navigate, currentLevel]);

  const handlePrev = useCallback(() => {
    if (selectedLevelIndex > 0) setSelectedLevelIndex((i) => i - 1);
  }, [selectedLevelIndex]);

  const handleNext = useCallback(() => {
    if (selectedLevelIndex < levels.length - 1) setSelectedLevelIndex((i) => i + 1);
  }, [selectedLevelIndex]);

  if (componentError) {
    return (
      <div className="app-homepage-premium">
        <div className="error-container">
          <h3>Error Loading Homepage</h3>
          <p>{componentError.message}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      </div>
    );
  }

  const mediaUrl = currentLevel?.image || FALLBACK_IMAGE;
  const mediaType = getMediaType(mediaUrl);

  return (
    <div className="app-homepage-premium">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Background: level image/gif/video + radial vignette (mirrors LevelPage) */}
      <div className="chapter-background-layer">
        {mediaType === 'video' ? (
          <video
            key={mediaUrl}
            src={mediaUrl}
            autoPlay
            muted
            loop
            playsInline
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.nextElementSibling;
              if (fallback) fallback.style.display = 'block';
            }}
          />
        ) : null}
        <img
          src={mediaType === 'video' ? FALLBACK_IMAGE : mediaUrl}
          alt=""
          aria-hidden
          style={mediaType === 'video' ? { display: 'none' } : undefined}
          onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
        />
      </div>
      <div className="chapter-gradient-overlay" aria-hidden />

      {/* Main layout — uses LevelPage CSS classes for visual continuity */}
      <div className="level-main-content">
        <div className="level-brand">NUNCHI</div>

        <div className="chapter-title-box">
          <div className="chapter-level-label">Select your level</div>
          <div className="chapter-main-title">{currentLevel?.korean}</div>
          <div className="chapter-subtitle">{currentLevel?.title}</div>
        </div>

        <div className="chapter-menu-vertical">
          <button onClick={() => navigate('/map')}>
            <i className="fas fa-map"></i>
            <span>Map</span>
          </button>
          <button onClick={() => navigate('/about')}>
            <i className="fas fa-info-circle"></i>
            <span>About</span>
          </button>
          <button onClick={() => console.log('Profile page not implemented yet')}>
            <i className="fas fa-user"></i>
            <span>Profile</span>
          </button>
          <button onClick={() => setIsSidebarOpen(true)}>
            <i className="fas fa-cog"></i>
            <span>Settings</span>
          </button>
        </div>

        <div className="chapter-nav-actions">
          <button className="chapter-enter-btn" onClick={handleEnter}>
            들어가기
          </button>
          <div className="chapter-nav-arrows">
            {selectedLevelIndex > 0 && (
              <button onClick={handlePrev} aria-label="Previous">
                ← 이전
              </button>
            )}
            {selectedLevelIndex < levels.length - 1 && (
              <button onClick={handleNext} aria-label="Next">
                다음 →
              </button>
            )}
          </div>
        </div>

        <div className="chapter-timeline-mini">
          <span>
            Level{' '}
            <span className="chapter-timeline-mini-strong">{selectedLevelIndex + 1}</span>
            {' / '}
            {levels.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AppHomepage;
